import cn from 'classnames';
import { useRef } from 'react';
import { FieldErrors, FieldValues, UseFormRegister } from 'react-hook-form';

import { Attachment } from '@/components/Icons';
import { DataFromFormInputs } from '@/types';

import s from './Upload.module.scss';

type UploadProps = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  label: string;
  name: string;
  errors: FieldErrors<DataFromFormInputs>;
  accept?: string;
  register: UseFormRegister<FieldValues>;
  required?: boolean;
  validationFileSize?: number;
  changeFileName: (name: string) => void;
};

const fileValidationFunction = (
  fileList: FileList,
  fileSize: number,
  required: boolean,
) => {
  const uploadedFile = Array.from(fileList);
  if (required === false) if (uploadedFile.length < 1) return true;
  if (uploadedFile.length < 1) return 'Загрузите файл';
  const bigSize = uploadedFile.find((file) => file.size > fileSize);
  if (bigSize)
    return `Слишком большой размер файла "${bigSize.name.slice(
      0,
      20,
    )}....${bigSize.name.split('.').at(-1)}"`;
  return true;
};

const Upload = ({
  label,
  name,
  errors,
  accept,
  register,
  required = false,
  changeFileName,
  validationFileSize = 20000000,
  ...rest
}: UploadProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const {
    ref,
    onChange: customChange,
    ...restOptions
  } = register(name, {
    validate: {
      fileValidation: (fileList: FileList) => {
        return fileValidationFunction(fileList, validationFileSize, required);
      },
    },
  });

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      changeFileName(event.target.files[0].name);
    }
  };

  const errorCondition = () => {
    if (name in errors) {
      const message = errors[name]?.message;

      if (message?.length && message?.length > 0) {
        return message;
      } else {
        return 'Обязательное поле';
      }
    }

    return null;
  };

  return (
    <div className={cn(s.root, { [s.error]: errors && errors[name] })}>
      <label className={s.label} htmlFor={name}>
        <span className={s.labelName}>{label}</span>
        <input
          ref={(el) => {
            inputRef.current = el;
            ref(el);
          }}
          className={s.input}
          style={{ display: 'none' }}
          id={name}
          type="file"
          accept={accept}
          {...restOptions}
          {...rest}
          onChange={(event) => {
            onChangeHandler(event);
            customChange(event);
          }}
          name={name}
        />
        <Attachment className={s.icon} />
      </label>
      {errors && errors[name] && (
        <span className={s.errorText}>{errorCondition()}</span>
      )}
    </div>
  );
};

export { Upload };
